import { RoleEnum } from 'ap-graphql';

import { useLoginInfo } from '@/components/LoginInfo/provider';
import { ErrorBoundary } from '@/components/Rollbar';
import { SuspenseLoading } from '@/components/SuspenseErrorBoundary/fallback';
import {
  FetchImportExportHistoriesArgs,
  useGetImportExportHistorySources,
} from '@/context/services/import_export_histories/ImportExportHistories.service';
import { SearchErrorFallback } from '@/features/import_export_histories/Search/components/SearchErrorFallback';
import { SearchForm } from '@/features/import_export_histories/Search/components/SearchForm';
import { useOfficeMembersInitialData } from '@/features/import_export_histories/Search/hooks/useInitialData';
import { useParseSearchParams } from '@/features/import_export_histories/Search/hooks/useParseSearchParams';
import { useTypeOptions } from '@/features/import_export_histories/Search/hooks/useTypeOptions';
import { FC, memo, Suspense, useMemo } from 'react';

type SearchProps = {
  onSearch: (query: FetchImportExportHistoriesArgs) => void;
  isLoading?: boolean;
};

export const Search: FC<SearchProps> = memo(({ onSearch, isLoading }) => {
  const initialValues = useParseSearchParams();
  const typeOptions = useTypeOptions();
  const sourceOptions = useGetImportExportHistorySources();
  const officeMemberDefaultOptions = useOfficeMembersInitialData(
    initialValues?.office_member_ids ?? []
  );
  const loginInfo = useLoginInfo();
  const showExecutedByFilter = useMemo(() => {
    const role = loginInfo?.currentOfficeMember?.officeMemberSetting?.role;
    return role === RoleEnum.Admin || role === RoleEnum.Observer;
  }, [loginInfo]);

  return (
    <Suspense fallback={<SuspenseLoading />}>
      <ErrorBoundary
        fallback={({ error }) => <SearchErrorFallback error={error} />}
      >
        <SearchForm
          onSearch={onSearch}
          typeOptions={typeOptions}
          sourceOptions={sourceOptions}
          officeMemberDefaultOptions={officeMemberDefaultOptions}
          initialValues={initialValues}
          isLoading={isLoading}
          showExecutedByFilter={showExecutedByFilter}
        />
      </ErrorBoundary>
    </Suspense>
  );
});
Search.displayName = 'Search';
